import {useRef, useState} from 'react';
import {flushSync} from 'react-dom';

import {isNil} from 'ramda';

import {useOnMount} from 'shared/web';

import {capturePdfPreviewAnchor} from '../utils/capturePdfPreviewAnchor';
import {getPdfPreviewScrollState} from '../utils/getPdfPreviewScrollState';
import {getPdfPreviewZoomStep} from '../utils/getPdfPreviewZoomStep';

export function usePdfPreviewScroll(widestPageWidth: number) {
  const viewport = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const pages = useRef(new Map<number, HTMLDivElement>());
  const selectedPage = useRef<{pageNumber: number; scrollTop: number} | null>(null);
  const currentPage = useRef(1);
  const cleanup = useRef<(() => void) | undefined>(undefined);
  const manualZoom = useRef(false);
  const fitScale = useRef(1);
  const activeScale = useRef(1);
  const [scale, setScale] = useState(1);
  const [view, setView] = useState({pageNumber: 1, firstRenderedPage: 1, lastRenderedPage: 1});

  const updateView = () => {
    const element = viewport.current;
    if (isNil(element)) {
      return;
    }
    if (selectedPage.current?.scrollTop !== element.scrollTop) {
      selectedPage.current = null;
    }
    const next = getPdfPreviewScrollState(
      element,
      Array.from(pages.current.values()),
      currentPage.current
    );
    if (!isNil(next)) {
      const nextView = {...next, pageNumber: selectedPage.current?.pageNumber ?? next.pageNumber};
      currentPage.current = nextView.pageNumber;
      setView((current) =>
        current.pageNumber === nextView.pageNumber &&
        current.firstRenderedPage === nextView.firstRenderedPage &&
        current.lastRenderedPage === nextView.lastRenderedPage
          ? current
          : nextView
      );
    }
  };

  const changeScale = (nextScale: number) => {
    const element = viewport.current;
    if (isNil(element) || activeScale.current === nextScale) {
      return;
    }
    const anchor = capturePdfPreviewAnchor(element, Array.from(pages.current.values()));
    activeScale.current = nextScale;
    // Commit layout within this interaction before measuring the new page geometry.
    flushSync(() => setScale(nextScale));
    if (!isNil(anchor)) {
      element.scrollLeft =
        anchor.page.offsetLeft + anchor.x * anchor.page.offsetWidth - element.clientWidth / 2;
      element.scrollTop =
        anchor.page.offsetTop + anchor.y * anchor.page.offsetHeight - element.clientHeight / 2;
    }
    selectedPage.current = {pageNumber: currentPage.current, scrollTop: element.scrollTop};
    updateView();
  };

  const measureFitScale = () => {
    const element = content.current;
    if (isNil(element)) {
      return fitScale.current;
    }
    const style = getComputedStyle(element);
    const padding =
      (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
    const availableWidth = element.clientWidth - padding;
    return availableWidth > 0 ? Math.min(1, availableWidth / widestPageWidth) : fitScale.current;
  };

  useOnMount(
    () => {
      const element = viewport.current;
      const contentElement = content.current;
      if (isNil(element) || isNil(contentElement)) {
        return;
      }
      fitScale.current = measureFitScale();
      activeScale.current = fitScale.current;
      setScale(fitScale.current);
      let frame: number | null = null;
      let layoutChanged = false;
      const scheduleUpdate = () => {
        if (isNil(frame)) {
          frame = requestAnimationFrame(() => {
            frame = null;
            if (layoutChanged && !isNil(selectedPage.current)) {
              selectedPage.current = {...selectedPage.current, scrollTop: element.scrollTop};
            }
            layoutChanged = false;
            fitScale.current = measureFitScale();
            if (!manualZoom.current) {
              changeScale(fitScale.current);
            }
            updateView();
          });
        }
      };
      const observer = new ResizeObserver(() => {
        layoutChanged = true;
        scheduleUpdate();
      });
      observer.observe(element);
      observer.observe(contentElement);
      element.addEventListener('scroll', scheduleUpdate, {passive: true});
      updateView();
      cleanup.current = () => {
        observer.disconnect();
        element.removeEventListener('scroll', scheduleUpdate);
        if (!isNil(frame)) {
          cancelAnimationFrame(frame);
        }
      };
    },
    () => cleanup.current?.()
  );

  const changePage = (pageNumber: number) => {
    const element = viewport.current;
    const page = pages.current.get(pageNumber);
    if (isNil(element) || isNil(page)) {
      return;
    }
    element.scrollTop = page.offsetTop;
    selectedPage.current = {pageNumber, scrollTop: element.scrollTop};
    updateView();
  };

  const zoom = (direction: 'in' | 'out') => {
    const nextScale = getPdfPreviewZoomStep(activeScale.current, direction);
    if (!isNil(nextScale)) {
      manualZoom.current = true;
      changeScale(nextScale);
    }
  };

  const resetZoom = () => {
    manualZoom.current = false;
    fitScale.current = measureFitScale();
    changeScale(fitScale.current);
  };

  return {
    viewport,
    content,
    pages,
    view,
    scale,
    changePage,
    resetZoom,
    zoomIn: () => zoom('in'),
    zoomOut: () => zoom('out'),
    canZoomIn: !isNil(getPdfPreviewZoomStep(scale, 'in')),
    canZoomOut: !isNil(getPdfPreviewZoomStep(scale, 'out')),
  };
}
